import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Save, Camera, ExternalLink, User, Globe, Lock, Award, Bell, CreditCard, ShoppingBag, ChevronRight, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import Brand from "../../components/Brand";

const CATEGORIES = [
  ["apologist",     "Apologist"],
  ["wellness",      "Wellness Coach"],
  ["bible_teacher", "Bible Teacher"],
  ["homeschool",    "Homeschool / Family"],
  ["marketplace",   "Christian Marketplace"],
  ["ministry",      "Pastor / Ministry"],
  ["coach",         "Christian Coach"],
  ["other",         "Other / Member"],
];

const SECTIONS = [
  { key: "profile",       label: "Profile",        Icon: User },
  { key: "password",      label: "Password",       Icon: Lock },
  { key: "certificates",  label: "Certificates",   Icon: Award },
  { key: "courses",       label: "My Courses",     Icon: Award },
  { key: "notifications", label: "Notifications",  Icon: Bell },
  { key: "billing",       label: "Billing",        Icon: CreditCard },
  { key: "orders",        label: "Order History",  Icon: ShoppingBag },
];

/* ============== PROFILE editor (uses /me/profile) ============== */
function ProfileSection() {
  const { user, refresh } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [form, setForm] = useState({ full_name: "", username: "", headline: "", bio: "", profile_photo: "", profile_category: "other", location: "", public_funnel_id: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      full_name: user.full_name || "",
      username: user.username || "", headline: user.headline || "", bio: user.bio || "",
      profile_photo: user.profile_photo || "", profile_category: user.profile_category || "other",
      location: user.location || "", public_funnel_id: user.public_funnel_id || "",
    });
  }, [user]);
  useEffect(() => { api.get("/funnels").then(r => setFunnels((r.data.funnels || []).filter(f => f.status === "published"))).catch(()=>{}); }, []);

  const handlePhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (8MB max)."); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setForm(f => ({ ...f, profile_photo: r.data.url }));
      toast.success("Photo updated.");
    } catch { toast.error("Could not upload photo."); }
    setUploading(false);
  };
  const submit = async (e) => {
    e?.preventDefault?.(); setSaving(true);
    try { await api.post("/me/profile", form); toast.success("Profile saved."); await refresh?.(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not save."); }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="profile-section">
      <div className="rounded-2xl bg-gradient-to-br from-[#FFFFFF] to-[#EAF6FC] border-2 border-[#F39A1F]/40 p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[#F39A1F] bg-white flex items-center justify-center shrink-0">
            {form.profile_photo ? <img src={form.profile_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-10 h-10 text-foreground/40"/>}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ YOUR PUBLIC PROFILE</div>
            <h2 className="font-serif-display text-2xl md:text-3xl uppercase break-all leading-tight"><Brand suffix=".com"/><span className="text-[#0F172A]">/u/</span><span className="text-[#2E9F3A] font-bold">{form.username || "yourname"}</span></h2>
            <div className="text-xs text-foreground/85 mt-1">Don't like the auto-generated name? <strong>Edit the URL slug field below</strong> to rename it.</div>
            {form.username && <Link to={`/u/${form.username}`} target="_blank" data-testid="profile-view-public" className="inline-flex items-center gap-1.5 mt-2 text-[11px] tracking-wider-cap font-bold text-[#2E9F3A] hover:underline uppercase"><ExternalLink className="w-3 h-3"/>View My Public Page</Link>}
          </div>
        </div>
      </div>

      <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
        <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Profile photo</label>
        <div className="flex items-center gap-4 mt-2">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-foreground/15 bg-white flex items-center justify-center shrink-0">
            {form.profile_photo ? <img src={form.profile_photo} alt="" className="w-full h-full object-cover"/> : <User className="w-12 h-12 text-foreground/40"/>}
          </div>
          <label className="cursor-pointer bg-[#F39A1F] text-[#0F172A] hover:bg-[#E5B82B] px-4 py-2.5 rounded-md text-[11px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-1.5 shadow transition">
            <Camera className="w-4 h-4"/>{uploading ? "Uploading…" : (form.profile_photo ? "Change photo" : "Upload photo")}
            <input type="file" accept="image/*" className="hidden" onChange={e=>handlePhoto(e.target.files?.[0])} data-testid="profile-photo-input"/>
          </label>
          {form.profile_photo && <button type="button" onClick={() => setForm(f=>({...f, profile_photo:""}))} className="text-[11px] tracking-wider-cap font-bold text-foreground/70 hover:text-red-wine uppercase">Remove</button>}
        </div>
      </div>

      <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
        <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Display Name · <span className="text-foreground/60 normal-case font-normal">how the site greets you</span></label>
        <input type="text" value={form.full_name} onChange={e=>setForm(f=>({...f, full_name: e.target.value}))} data-testid="profile-full-name" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none transition" placeholder="e.g. Michael Boyd" maxLength={80}/>
      </div>

      <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
        <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">URL slug · <span className="text-foreground/60 normal-case font-normal">lowercase letters, numbers, dashes</span></label>
        <div className="flex mt-2 rounded-md border-2 border-foreground/20 focus-within:border-[#F39A1F] overflow-hidden">
          <span className="bg-white text-foreground/70 px-3 py-2.5 text-sm border-r border-foreground/15 whitespace-nowrap"><Brand suffix=".com"/>/u/</span>
          <input type="text" value={form.username} onChange={e=>setForm(f=>({...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"")}))} data-testid="profile-username" className="flex-1 px-3 py-2.5 text-sm outline-none bg-white" placeholder="your-name" maxLength={32}/>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
          <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Headline</label>
          <input type="text" maxLength={100} value={form.headline} onChange={e=>setForm(f=>({...f, headline: e.target.value}))} placeholder='e.g. "Christian Wellness Coach · Texas"' data-testid="profile-headline" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none transition"/>
        </div>
        <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
          <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Category</label>
          <select value={form.profile_category} onChange={e=>setForm(f=>({...f, profile_category: e.target.value}))} data-testid="profile-category" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm bg-white outline-none transition">
            {CATEGORIES.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
          <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">About you · <span className="text-foreground/60 normal-case font-normal">2-3 sentences</span></label>
          <textarea rows={4} maxLength={400} value={form.bio} onChange={e=>setForm(f=>({...f, bio: e.target.value}))} placeholder="Tell visitors who you are and why you joined." data-testid="profile-bio" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none transition"/>
          <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 mt-1">{form.bio.length}/400</div>
        </div>
        <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
          <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Location</label>
          <input type="text" maxLength={80} value={form.location} onChange={e=>setForm(f=>({...f, location: e.target.value}))} placeholder='e.g. "Austin, TX"' data-testid="profile-location" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none transition"/>
        </div>
      </div>

      <div className="rounded-xl border-2 border-[#F39A1F]/40 bg-white/60 p-5">
        <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep inline-flex items-center gap-1.5"><Globe className="w-3 h-3"/>Public funnel — shows on your profile + the homepage Directory</label>
        {funnels.length === 0 ? (
          <div className="mt-2 text-sm text-foreground italic">You don't have any <strong>published</strong> funnels yet. Build one in Funnels, click "Save & Publish", then come back here.</div>
        ) : (
          <select value={form.public_funnel_id || ""} onChange={e=>setForm(f=>({...f, public_funnel_id: e.target.value || null}))} data-testid="profile-public-funnel" className="w-full mt-2 border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm bg-white outline-none transition">
            <option value="">— Don't show me in the Directory yet —</option>
            {funnels.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        )}
      </div>

      <button type="submit" disabled={saving} data-testid="profile-save" className="w-full md:w-auto bg-[#2E9F3A] hover:bg-[#1F8A2A] disabled:opacity-60 text-white px-7 py-3 rounded-md font-bold uppercase tracking-wider-cap text-sm inline-flex items-center justify-center gap-2 shadow-lg transition">
        <Save className="w-4 h-4"/>{saving ? "Saving…" : "Save Profile"}
      </button>
    </form>
  );
}

/* ============== PASSWORD ============== */
function PasswordSection() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password.");
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4 max-w-lg" data-testid="password-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">Change Password</h2>
      <p className="text-foreground text-sm">Enter your current password, then choose a new one (8+ characters).</p>
      {[
        ["Current password", cur, setCur, "password-current"],
        ["New password", next, setNext, "password-new"],
        ["Confirm new password", confirm, setConfirm, "password-confirm"],
      ].map(([label, val, set, tid]) => (
        <div key={tid} className="rounded-xl border-2 border-foreground/15 bg-white p-4">
          <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep">{label}</label>
          <div className="relative mt-2">
            <input type={show ? "text" : "password"} value={val} onChange={e=>set(e.target.value)} required data-testid={tid} className="w-full border-2 border-foreground/20 focus:border-[#F39A1F] rounded-md px-3 py-2.5 text-sm outline-none pr-10 transition"/>
            <button type="button" onClick={() => setShow(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/60 hover:text-red-wine">{show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</button>
          </div>
        </div>
      ))}
      <button type="submit" disabled={busy} data-testid="password-save" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] disabled:opacity-60 text-white px-6 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-sm inline-flex items-center gap-2 shadow-lg transition">
        <Lock className="w-4 h-4"/>{busy ? "Saving…" : "Update Password"}
      </button>
    </form>
  );
}

/* ============== CERTIFICATES + COURSES ============== */
function CertificatesSection() {
  const [certs, setCerts] = useState([]);
  useEffect(() => { api.get("/certificates").then(r => setCerts(r.data.certificates || [])).catch(()=>{}); }, []);
  return (
    <div className="space-y-4" data-testid="certificates-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">Diplomas Awarded</h2>
      {certs.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-8 text-center">
          <Award className="w-12 h-12 text-[#F39A1F] mx-auto mb-2"/>
          <div className="font-serif-display text-2xl text-red-wine">No diplomas yet.</div>
          <div className="text-foreground text-sm mt-1">Complete a course in <Link to="/dashboard/courses" className="text-[#2E9F3A] font-bold hover:underline">Academy</Link> to earn your first diploma.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {certs.map(c => (
            <div key={c.id} className="rounded-xl border-2 border-[#F39A1F]/40 bg-gradient-to-br from-[#FFFFFF] to-white p-5">
              <Award className="w-8 h-8 text-[#F39A1F] mb-2"/>
              <div className="font-bold text-red-wine">{c.course_title}</div>
              <div className="text-[11px] text-foreground/70">Awarded {new Date(c.awarded_at).toLocaleDateString()}</div>
              <div className="text-[10px] tracking-wider-cap font-bold text-foreground/60 mt-2">CERT ID · {c.id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoursesProgressSection() {
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/courses").then(r => setCourses((r.data.courses || []).filter(c => c.enrolled))).catch(()=>{}); }, []);
  return (
    <div className="space-y-4" data-testid="courses-progress-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">My Courses & Progress</h2>
      {courses.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[#F39A1F]/40 bg-white/40 p-8 text-center">
          <div className="font-serif-display text-2xl text-red-wine">You haven't enrolled in any course yet.</div>
          <Link to="/dashboard/courses" className="btn-primary inline-block mt-4">Browse Academy</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {courses.map(c => (
            <div key={c.id} className="rounded-xl border-2 border-foreground/15 bg-white p-4 flex items-center gap-4 flex-wrap">
              <img src={c.image} className="w-20 h-20 object-cover rounded-md shrink-0" alt=""/>
              <div className="flex-1 min-w-[200px]">
                <div className="font-bold text-red-wine">{c.title}</div>
                <div className="text-[11px] text-foreground/70">{c.completed_lessons.length}/{c.lessons.length} lessons · {c.progress}% complete</div>
                <div className="h-2 bg-foreground/10 rounded-full overflow-hidden mt-2">
                  <div className="h-full bg-gradient-to-r from-[#3B7A3B] via-[#F39A1F] to-[#2E9F3A] transition-all" style={{ width: `${c.progress}%` }}/>
                </div>
              </div>
              <Link to={`/dashboard/courses`} className="text-[10px] tracking-wider-cap font-bold text-[#2E9F3A] hover:underline uppercase">Continue →</Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============== NOTIFICATIONS ============== */
function NotificationsSection() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/me/notifications").then(r => setPrefs(r.data.prefs)).catch(()=>{}); }, []);
  if (!prefs) return <div className="text-foreground/60">Loading…</div>;

  const toggle = async (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next); setBusy(true);
    try { await api.post("/me/notifications", { [k]: next[k] }); }
    catch { toast.error("Could not save."); setPrefs(prefs); }
    setBusy(false);
  };
  const TOGGLES = [
    ["email_broadcasts",        "Faitharama broadcasts",        "Mike's weekly note + product news"],
    ["email_helpdesk_replies",  "Helpdesk reply emails",        "When someone replies to your ticket"],
    ["email_community_replies", "Community comment replies",    "When someone responds to your post"],
    ["email_advance_copy",      "Book launch updates",          "When 'With Faith All Things Are Possible' drops"],
  ];
  return (
    <div className="space-y-4" data-testid="notifications-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">Email Notifications</h2>
      <p className="text-foreground text-sm">Pick which <Brand/> emails you want. We respect your inbox.</p>
      <div className="space-y-2">
        {TOGGLES.map(([k, label, sub]) => (
          <button key={k} onClick={() => toggle(k)} disabled={busy} data-testid={`notify-${k}`} className="w-full text-left rounded-xl border-2 border-foreground/15 bg-white p-4 flex items-center gap-4 hover:border-[#F39A1F] transition">
            <div className="flex-1">
              <div className="font-bold text-red-wine">{label}</div>
              <div className="text-[11px] text-foreground/75">{sub}</div>
            </div>
            <div className={`w-12 h-7 rounded-full p-0.5 transition ${prefs[k] ? "bg-[#3B7A3B]" : "bg-foreground/15"}`}>
              <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${prefs[k] ? "translate-x-5" : ""}`}/>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============== BILLING ============== */
function BillingSection() {
  const { user } = useAuth();
  if (!user) return null;
  const isFree = user.tier === "free";
  return (
    <div className="space-y-4" data-testid="billing-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">Plan & Billing</h2>
      <div className="rounded-2xl border-2 border-[#F39A1F]/40 bg-gradient-to-br from-[#FFFFFF] to-[#EAF6FC] p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">CURRENT PLAN</div>
            <div className="font-serif-display text-3xl text-red-wine uppercase">{isFree ? "7-Day Free Trial" : "All Access"}</div>
            <div className="text-[11px] tracking-wider-cap font-bold text-foreground/85 mt-1">STATUS · {(user.subscription_status || "trialing").toUpperCase()}</div>
            {user.trial_ends_at && !isFree && <div className="text-sm text-foreground mt-1">Trial ends · {new Date(user.trial_ends_at).toLocaleDateString()}</div>}
          </div>
          <div className="text-right">
            <div className="text-4xl text-red-wine">${isFree ? "0" : "29"}</div>
            <div className="text-[11px] tracking-wider-cap font-bold text-foreground/70">{isFree ? "FOREVER" : "/MONTH"}</div>
          </div>
        </div>
        <div className="mt-5 flex gap-2 flex-wrap">
          {isFree
            ? <Link to="/pricing" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-5 py-2.5 rounded-md text-[11px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-2 shadow transition" data-testid="billing-upgrade-cta">Upgrade To All Access · $49/mo <ChevronRight className="w-4 h-4"/></Link>
            : <Link to="/pricing" className="bg-white border-2 border-foreground/20 hover:border-[#F39A1F] text-foreground px-5 py-2.5 rounded-md text-[11px] tracking-wider-cap font-bold uppercase inline-flex items-center gap-2 transition">View Plan</Link>
          }
        </div>
      </div>
      <div className="rounded-xl border-2 border-foreground/15 bg-white p-5">
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">PAYMENT METHOD</div>
        <div className="text-foreground mt-1">Managed by Stripe — secure, 1-tap update from your invoice email.</div>
        <div className="text-[11px] text-foreground/70 mt-2">To change card or cancel: open any <Brand/> invoice email and click "Manage subscription". A self-serve portal is on the way.</div>
      </div>
    </div>
  );
}

/* ============== ORDER HISTORY ============== */
function OrdersSection() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/me/orders").then(r => setOrders(r.data.orders || [])).catch(()=>{}); }, []);
  return (
    <div className="space-y-4" data-testid="orders-section">
      <h2 className="font-serif-display text-2xl text-red-wine uppercase">Order History</h2>
      {orders.length === 0 ? (
        <div className="text-foreground">No orders yet.</div>
      ) : (
        <div className="rounded-xl border-2 border-foreground/15 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0F172A] text-white">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] tracking-wider-cap">Item</th>
                <th className="text-left px-4 py-2 text-[10px] tracking-wider-cap">Type</th>
                <th className="text-left px-4 py-2 text-[10px] tracking-wider-cap">Status</th>
                <th className="text-right px-4 py-2 text-[10px] tracking-wider-cap">Price</th>
                <th className="text-right px-4 py-2 text-[10px] tracking-wider-cap">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => (
                <tr key={o.id} className={i%2===0 ? "bg-[#EAF6FC]" : ""}>
                  <td className="px-4 py-3 font-bold text-red-wine">{o.title}</td>
                  <td className="px-4 py-3 text-foreground/85 capitalize">{o.kind}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-[10px] tracking-wider-cap font-bold text-[#3B7A3B] uppercase"><CheckCircle2 className="w-3 h-3"/>{o.status}</span></td>
                  <td className="px-4 py-3 text-right text-foreground">${o.price}</td>
                  <td className="px-4 py-3 text-right text-foreground/80 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-foreground/70 italic">Stripe-charged purchases will show up here once course-checkout opens.</div>
    </div>
  );
}

/* ============== HUB ============== */
export default function ProfileTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = (searchParams.get("section") || "profile").toLowerCase();
  const validKeys = SECTIONS.map(s => s.key);
  const [section, setSection] = useState(validKeys.includes(initial) ? initial : "profile");

  // Keep URL in sync so a refresh keeps you on the same sub-page
  useEffect(() => {
    if (section === "profile") {
      const next = new URLSearchParams(searchParams);
      next.delete("section");
      setSearchParams(next, { replace: true });
    } else if (searchParams.get("section") !== section) {
      const next = new URLSearchParams(searchParams);
      next.set("section", section);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const renderSection = () => {
    switch (section) {
      case "profile":       return <ProfileSection/>;
      case "password":      return <PasswordSection/>;
      case "certificates":  return <CertificatesSection/>;
      case "courses":       return <CoursesProgressSection/>;
      case "notifications": return <NotificationsSection/>;
      case "billing":       return <BillingSection/>;
      case "orders":        return <OrdersSection/>;
      default:              return <ProfileSection/>;
    }
  };

  return (
    <div data-testid="profile-tab" className="grid lg:grid-cols-[260px_1fr] gap-6">
      {/* Sub-nav (left side, like Mike's screenshot) */}
      <aside className="lg:sticky lg:top-6 self-start" data-testid="profile-subnav">
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep mb-2">★ MY ACCOUNT</div>
        <nav className="rounded-xl border-2 border-foreground/15 bg-white overflow-hidden">
          {SECTIONS.map(s => {
            const active = section === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`profile-subnav-${s.key}`}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-foreground/10 last:border-b-0 transition ${active ? "bg-white text-red-wine border-l-4 border-l-[#2E9F3A]" : "text-foreground hover:bg-white"}`}
              >
                <s.Icon className={`w-4 h-4 ${active ? "text-red-wine" : "text-foreground/60"}`}/>
                <span className="text-sm font-semibold flex-1">{s.label}</span>
                {active && <ChevronRight className="w-4 h-4"/>}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Section content */}
      <div data-testid="profile-section-content">{renderSection()}</div>
    </div>
  );
}
